import {
    call,
    put,
    select,
    fork
} from 'redux-saga/effects';

import {
    debug
} from '../../../core/js/utils/logger';

import {
    TokenClient
} from 'urbnTokenLib';

import {
    LoginUtility,
    ProfileClient
} from '../../../core/js/loginUtility/loginUtility';

import {
    receiveLoyaltySubscription,
    receiveUserAddresses
} from '../../../core/js/actions/userActions.js';

import {
    loyaltyLoginSuccess,
    loyaltySignupSuccess,
} from '../actions/loyaltyActions.js';

import { makeRequest } from '../../../core/js/sagas/makeRequest';
import apiRoutes from '../../../core/js/utils/apiRoutes';
import { createSubscriptionsIfNeeded } from '../../../core/js/sagas/userSagas';
import {
    LOGIN_METHOD_SIGNUP,
    GENDER_TYPES
} from '../../../core/js/constants';

import { createGAEvent } from '../../../core/js/utils/analyticsUtils';
import { handleUtagEvent } from '../../../core/js/actions/initialActions';

import { modalShepherdSingleton } from '../../../core/js/components/modalShepherdComponent';

/**
 * Calls the profile availability endpoint to see if the user already has an existing profile
 * @param {string} username - email address value entered in loyalty form
 */
export function* profileAvailabilitySaga(username) {
    debug('Saga: profileAvailabilitySaga initialized');

    const state = yield select();
    const route = apiRoutes.getProfileAvailability(state, username);
    yield call(makeRequest, route);
}

/**
 * Calls the proper loyalty signup handler depending on which form type was submitted
 * @param {object} payload - loyalty form payload
 * @param {string} formType - type of loyalty form (longForm, shortForm, fullForm)
 */
export function* loyaltySignupSaga(payload, formType) {
    debug('Saga: loyaltySignupSaga initialized');

    switch(formType) {
        case 'longForm':
            yield handleLongFormSignup(payload);
            break;
        case 'shortForm':
            yield handleShortFormSignup(payload);
            break;
        default:
            yield handleFullFormSignup(payload);
    }
}

/**
 * POST to profile web preferences endpoint with interstitialDeclined set to true
 */
export function* loyaltyInterstitialDeclinedSaga() {
    debug('Saga: loyaltyInterstitialDeclinedSaga initialized');

    const state = yield select();
    const entity = {
        'interstitialDeclined': true
    };
    const route = apiRoutes.updateProfileWebPreferences(state, entity);
    yield call(makeRequest, route);
}

function* addLoyaltySignupSuccessEvent(formType) {
    const gaEvent = createGAEvent('PAGE_TYPE', 'loyalty signup submit', `signup complete ${formType}`);
    yield put(handleUtagEvent(gaEvent, 'link'));
}

/**
 * Loyalty full form signup handler (guest user) - creates user profile, signs
 * user in, adds user address and creates user loyalty subscription
 * @param {object} payload - loyalty form payload
 */
function* handleFullFormSignup(payload) {
    debug('handleFullFormSignup');

    let state = yield select();
    const loyaltyConfig = _.get(state, 'core.featureConfigs.loyalty-sign-up', {});
    const siteId = state.core.constants.siteId;
    const urls = state.core.constants.urls;
    const tokenClient = new TokenClient(siteId, urls.token);
    const profileClient = new ProfileClient(siteId, urls.profile);
    const loginUtility = new LoginUtility(tokenClient, profileClient);
    const birthdayPayload = getBirthdayPayload(payload);
    const profileNamePayload = {
        'name': {
            'last': payload.addressData.lastName,
            'first': payload.addressData.firstName
        }
    };
    const genderPayload = {
        'gender': payload.loginData.gender || GENDER_TYPES.UNKNOWN
    };
    const otherProfileData = Object.assign(
        {}, profileNamePayload, birthdayPayload, genderPayload
    );

    yield call(
        [
            loginUtility,
            loginUtility.signUp
        ],
        payload.loginData.emailAddress,
        payload.loginData.password,
        payload.loginData.confirmPassword,
        otherProfileData
    );
    yield put(loyaltyLoginSuccess());

    if (payload.loginData.emailSubscription) {
        yield call(handleEmailSubscription, payload);
    }

    if (loyaltyConfig.showAddressCapture) {
        state = yield select();
        yield addProfileAddress(state, payload.addressData);
        yield put(receiveUserAddresses());
    }

    state = yield select();
    const loyaltyRes = yield createLoyaltySubscription(state);
    yield put(receiveLoyaltySubscription(loyaltyRes.loyalty));
    if (loyaltyRes.loyalty) {
        yield put(loyaltySignupSuccess());
        yield call(addLoyaltySignupSuccessEvent, 'long');

        // let PWA know that we logged in to update the UI
        modalShepherdSingleton.outcome = {
            signupSuccessful: true,
        };
    }
}

/**
 * Loyalty long form signup handler (user with profile with no address) - adds
 * user address, updates birthday and creates user loyalty subscription
 * @param {object} payload - loyalty form payload
 */
function* handleLongFormSignup(payload) {
    debug('handleLongFormSignup');

    let state = yield select();
    const loyaltyConfig = _.get(state, 'core.featureConfigs.loyalty-sign-up', {});

    if (loyaltyConfig.showAddressCapture) {
        yield addProfileAddress(state, payload.addressData);
        yield put(receiveUserAddresses());
    }

    if (!state.core.profile.dateOfBirth) {
        state = yield select();
        yield updateProfileBirthday(state, payload);
    }

    if (payload.loginData.emailSubscription) {
        yield call(handleEmailSubscription, payload);
    }

    state = yield select();
    const loyaltyRes = yield createLoyaltySubscription(state);
    yield put(receiveLoyaltySubscription(loyaltyRes.loyalty));
    if (loyaltyRes.loyalty) {
        yield put(loyaltySignupSuccess());
        yield call(addLoyaltySignupSuccessEvent, 'short');
    }

    // let PWA know that we are enrolled in loyalty and update the UI
    modalShepherdSingleton.outcome = {
        loyaltySignupSuccessful: true,
    };
}

/**
 * Loyalty short form signup handler (user with profile and address) - updates
 * birthday and creates user loyalty subscription
 * @param {object} payload - loyalty form payload
 */
function* handleShortFormSignup(payload) {
    debug('handleShortFormSignup');

    let state = yield select();
    if (!state.core.profile.dateOfBirth) {
        yield updateProfileBirthday(state, payload);
    }

    if (payload.loginData.emailSubscription) {
        yield call(handleEmailSubscription, payload);
    }

    state = yield select();
    const loyaltyRes = yield createLoyaltySubscription(state);
    yield put(receiveLoyaltySubscription(loyaltyRes.loyalty));
    if (loyaltyRes.loyalty) {
        yield put(loyaltySignupSuccess());
        yield call(addLoyaltySignupSuccessEvent, 'short');
    }
}

/**
 * POST to create loyalty subscription
 * @param {object} state - current state of the store
 * @return {function} make request to create loyalty subscription endpoint
 */
function createLoyaltySubscription(state) {
    const route = apiRoutes.createLoyaltySubscription(state);
    return call(makeRequest, route);
}

/**
 * PUT to update profile with users birthday
 * @param {object} state - current state of the store
 * @param {object} payload - loyalty form payload
 * @return {function} make request to profile endpoint
 */
function updateProfileBirthday(state, payload) {
    const profilePayload = Object.assign(
        {}, { 'emailAddress': state.core.profile.emailAddress }, getBirthdayPayload(payload)
    );
    const route = apiRoutes.updateProfile(state, profilePayload);
    return call(makeRequest, route);
}

/**
 * POST to add profile address
 * @param {object} state - current state of the store
 * @param {object} payload Triggering action payload
 * @return {function} make request to profile address endpoint
 */
function addProfileAddress(state, payload) {
    const addressPayload = {
        'address': {
            'city': payload.city,
            'name': {
                'last': payload.lastName,
                'first': payload.firstName
            },
            'poBox': payload.poBox,
            'address1': payload.address1,
            'address2': payload.address2,
            'address3': payload.address3,
            'phoneNumber': payload.phone,
            'country': payload.country,
            'postalCode': payload.zip,
            'region': payload.state ? payload.state : ''
        }
    };
    const route = apiRoutes.addProfileAddress(state, addressPayload);
    return call(makeRequest, route);
}

/**
 * Format and return proper birthday payload for submission to profile service
 * @param {object} payload - loyalty form payload
 * @return {object} formatted birthday payload
 */
function getBirthdayPayload(payload) {
    const birthdayPayload = {
        'dateOfBirth': {
            'year': parseInt(payload.birthdayData.birthYear, 10),
            'day': parseInt(payload.birthdayData.birthDay, 10),
            'month': parseInt(payload.birthdayData.birthMonth, 10)
        }
    };

    return birthdayPayload;
}

function* handleEmailSubscription(payload) {
    const source = payload.loginData.source;
    const gender = payload.loginData.gender || GENDER_TYPES.UNKNOWN;
    const method = LOGIN_METHOD_SIGNUP;

    return yield fork(createSubscriptionsIfNeeded, method, source, gender);
}
